import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { LinkedItem } from '@types'
import {
  isFreeDimensionLateralOrCostadoCatalog,
  isIndependentZocaloLinkedSon,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { rebuildLinkedItemZonesFromUnit } from '@utils/sceneObjects/linked/rebuildLinkedItemZonesFromUnit'
import {
  collectValidZonesFromUnitAcabados,
  isTextureValidForUnitAcabados,
  sanitizeDisallowedCostadoOnLinkedItem,
} from '@utils/sceneObjects/linked/linkedZoneTextureMapping'
import type { Object3D } from 'three'

type SceneGlbEntry = {
  glb?: { scene?: { children?: unknown }; children?: unknown } | Object3D
  backGLBInfo?: {
    acabados?: Array<{
      idAcabadoUnidad?: number
      zona?: string
      descripcion?: string
    }>
    codigo?: string
  }
}

function collectGlbMeshNames(glb: SceneGlbEntry['glb']): Set<string> {
  const names = new Set<string>()
  if (!glb) return names

  const root = (glb as { scene?: Object3D }).scene ?? (glb as Object3D)
  if (!root || typeof (root as Object3D).traverse !== 'function') return names

  ;(root as Object3D).traverse((obj) => {
    const mesh = obj as Object3D & { isMesh?: boolean; name?: string }
    if (mesh.isMesh && mesh.name) names.add(String(mesh.name))
  })

  return names
}

function getGlbSceneChildren(glb: SceneGlbEntry['glb']): unknown {
  if (!glb) return undefined
  const withScene = glb as { scene?: { children?: unknown }; children?: unknown }
  return withScene.scene?.children ?? withScene.children
}

function isZocaloLogicalName(name?: string | null): boolean {
  return /z[oó]calo/i.test(String(name ?? ''))
}

/** Zócalo embebido en el mueble (GLB/zonas del padre), no el enlazado Zocalo 4000. */
export function itemHasEmbeddedZocalo(item: LinkedItem): boolean {
  if (isIndependentZocaloLinkedSon(item) || isFreeDimensionLateralOrCostadoCatalog(item)) {
    return false
  }

  const textures = item.textures ?? []
  if (
    textures.some(
      (texture) =>
        isZocaloLogicalName(texture.zoneDisplayName) ||
        isZocaloLogicalName(texture.zone) ||
        isZocaloLogicalName(texture.zoneName),
    )
  ) {
    return true
  }

  const zones = item.zones ?? {}
  for (const [key, zone] of Object.entries(zones)) {
    const mat = zone?.material as { zoneDisplayName?: string; zone?: string } | undefined
    if (
      isZocaloLogicalName(key) ||
      isZocaloLogicalName(zone?.name) ||
      isZocaloLogicalName(mat?.zoneDisplayName) ||
      isZocaloLogicalName(mat?.zone)
    ) {
      return true
    }
  }

  return false
}

function itemHasZocalo4000LinkedSon(
  item: LinkedItem,
  items: Map<string, unknown>,
): boolean {
  const ids = new Set<string>()
  if (typeof item.independentZocaloSonId === 'string' && item.independentZocaloSonId) {
    ids.add(item.independentZocaloSonId)
  }
  if (item.childs) {
    for (const childId of item.childs) ids.add(String(childId))
  }
  for (const id of ids) {
    const child = items.get(id)
    if (child && isIndependentZocaloLinkedSon(child as LinkedItem)) return true
  }
  return false
}

/**
 * Proyecto antiguo: algún mueble trae zócalo en el propio item y no tiene
 * Zocalo 4000 entre los enlazados. Si hay Zocalo 4000, el contrato ya es el nuevo.
 */
export function projectHasLegacyEmbeddedZocalo(items: Map<string, unknown>): boolean {
  for (const raw of items.values()) {
    if (!isLinkedItem(raw)) continue
    if (!itemHasEmbeddedZocalo(raw)) continue
    if (itemHasZocalo4000LinkedSon(raw, items)) continue
    return true
  }
  return false
}

function itemZonesDisagreeWithCurrentAcabados(
  item: LinkedItem,
  acabados: unknown,
): boolean {
  const valid = collectValidZonesFromUnitAcabados(acabados)
  if (valid.size === 0) return false
  return (item.textures ?? []).some(
    (texture) => !isTextureValidForUnitAcabados(texture, valid),
  )
}

/**
 * True when saved texture/zone mesh keys no longer exist in the current GLB
 * (e.g. RA905 Cube002_* → Cube001_* after catalog asset update).
 */
export function linkedItemHasStaleGlbZoneNames(
  item: Pick<LinkedItem, 'textures' | 'zones'>,
  glbMeshNames: Set<string>,
): boolean {
  if (glbMeshNames.size === 0) return false

  const textureZoneNames = (item.textures ?? [])
    .map((t) => (typeof t?.zoneName === 'string' ? t.zoneName.trim() : ''))
    .filter(Boolean)

  if (textureZoneNames.length > 0) {
    const matched = textureZoneNames.filter((name) => glbMeshNames.has(name)).length
    return matched === 0
  }

  const zoneKeys = Object.keys(item.zones ?? {}).filter((key) =>
    /^(Mesh|Cube)\d+/i.test(key),
  )
  if (zoneKeys.length === 0) return false
  return zoneKeys.every((key) => !glbMeshNames.has(key))
}

function buildAcabadosZonasExtra(
  acabados: NonNullable<SceneGlbEntry['backGLBInfo']>['acabados'],
): { zonas: { idAcabadoUnidad: number; zona: string }[] } | undefined {
  if (!Array.isArray(acabados) || acabados.length === 0) return undefined
  const zonas = acabados
    .filter((a) => a && a.idAcabadoUnidad && a.zona)
    .map((a) => ({
      idAcabadoUnidad: a.idAcabadoUnidad as number,
      zona: String(a.zona),
    }))
  return zonas.length > 0 ? { zonas } : undefined
}

export type RemapStaleZonesResult = {
  remappedCount: number
  remappedItemIds: string[]
}

function resolveSceneEntry(
  item: LinkedItem,
  sceneGLB: Map<string | number, SceneGlbEntry>,
): SceneGlbEntry | undefined {
  const catalogKey = item.catalogID || item.idUnidades
  if (catalogKey == null || catalogKey === '') return undefined
  return (
    sceneGLB.get(catalogKey) ||
    (item.catalogID != null ? sceneGLB.get(String(item.catalogID)) : undefined) ||
    (item.idUnidades != null ? sceneGLB.get(item.idUnidades) : undefined)
  )
}

function rebuildItemFromCurrentCatalog(
  item: LinkedItem,
  itemId: string,
  sceneEntry: SceneGlbEntry,
  source: string,
): void {
  const acabados = sceneEntry.backGLBInfo?.acabados
  const glbSceneChildren = getGlbSceneChildren(sceneEntry.glb)
  if (!Array.isArray(acabados) || acabados.length === 0 || !glbSceneChildren) return

  rebuildLinkedItemZonesFromUnit({
    item,
    newUnidadData: {
      acabados,
      codigo: String(item.catalogID ?? sceneEntry.backGLBInfo?.codigo ?? ''),
    },
    glbSceneChildren,
    acabadosZonasExtra: buildAcabadosZonasExtra(acabados),
    itemId,
    source,
  })
}

/**
 * Al abrir: si el proyecto es antiguo (zócalo en el GLB del mueble, no Zocalo 4000
 * enlazado), reconstruye zones/textures desde el catálogo actual — el mismo
 * contrato que un cambio de catálogo / toggle Gola. Preserva acabados de usuario
 * por zoneDisplayName (Puerta / Armazon / …).
 */
export function remapStaleLinkedZonesOnProjectLoad(
  items: Map<string, unknown>,
  sceneGLB: Map<string | number, SceneGlbEntry>,
): RemapStaleZonesResult {
  const result: RemapStaleZonesResult = {
    remappedCount: 0,
    remappedItemIds: [],
  }
  const projectIsLegacy = projectHasLegacyEmbeddedZocalo(items)

  for (const [itemId, raw] of items.entries()) {
    if (!isLinkedItem(raw)) continue
    const item = raw
    if (!Array.isArray(item.textures) || item.textures.length === 0) continue

    const sceneEntry = resolveSceneEntry(item, sceneGLB)
    const meshNames = sceneEntry?.glb ? collectGlbMeshNames(sceneEntry.glb) : null
    const acabados = sceneEntry?.backGLBInfo?.acabados
    const canRebuild =
      Boolean(sceneEntry) &&
      Array.isArray(acabados) &&
      acabados.length > 0 &&
      Boolean(getGlbSceneChildren(sceneEntry?.glb))

    const shouldRebuildAsCatalogChange =
      canRebuild &&
      (projectIsLegacy ||
        (itemHasEmbeddedZocalo(item) && !itemHasZocalo4000LinkedSon(item, items)) ||
        (meshNames != null && linkedItemHasStaleGlbZoneNames(item, meshNames)) ||
        itemZonesDisagreeWithCurrentAcabados(item, acabados))

    if (shouldRebuildAsCatalogChange && sceneEntry) {
      try {
        rebuildItemFromCurrentCatalog(
          item,
          itemId,
          sceneEntry,
          projectIsLegacy
            ? 'projectLoad:legacyEmbeddedZocalo'
            : 'projectLoad:remapStaleGlbZones',
        )
        result.remappedCount += 1
        result.remappedItemIds.push(itemId)
      } catch (error) {
        // console.warn(
          // `[remapStaleLinkedZonesOnProjectLoad] Fallo remapeando zonas de ${itemId}`,
          // error,
        // )
      }
    }

    if (sanitizeDisallowedCostadoOnLinkedItem(item) && !result.remappedItemIds.includes(itemId)) {
      result.remappedCount += 1
      result.remappedItemIds.push(itemId)
    }
  }

  return result
}
